import type {ServerWebExchange} from '../../../types/web/server';

export type MatchResult = Readonly<{ match: boolean, variables: Readonly<Record<string, string>> }>;

export interface ServerWebExchangeMatcher {
    /**
     * Matches the given exchange against the matcher.
     * @param exchange The web exchange to match.
     * @returns A promise that resolves to true if the exchange matches, false otherwise.
     */
    (exchange: ServerWebExchange): Promise<MatchResult>;

    /**
     * Returns a string representation of the matcher.
     * @returns A string representation of the matcher.
     */
    toString(): string;
}

export type ServerWebExchangeMatcherEntry<T> = Readonly<[ServerWebExchangeMatcher, T]>

export const or: (matchers: ServerWebExchangeMatcher[]) => ServerWebExchangeMatcher = (matchers: ServerWebExchangeMatcher[]): ServerWebExchangeMatcher => {
    return async (exchange: ServerWebExchange): Promise<MatchResult> => {
        for (const matcher of matchers) {
            const result = await matcher(exchange);
            if (result.match) {
                return match();
            }
        }
        return NO_MATCH;
    };
}

export const and: (matchers: ServerWebExchangeMatcher[]) => ServerWebExchangeMatcher = (matchers: ServerWebExchangeMatcher[])  => {
    const matcher = async (exchange: ServerWebExchange): Promise<MatchResult> => {
        for (const matcher of matchers) {
            const result = await matcher(exchange);
            if (!result.match) {
                return NO_MATCH;
            }
        }
        return match();
    };
    matcher.toString = () => `and(${matchers.map(m => m.toString()).join(', ')})`;
    return matcher;
}


export const not: (matcher: ServerWebExchangeMatcher) => ServerWebExchangeMatcher = (matcher: ServerWebExchangeMatcher): ServerWebExchangeMatcher => {
    return async (exchange: ServerWebExchange): Promise<MatchResult> => {
        const result = await matcher(exchange);
        return result.match ? NO_MATCH : match();
    };
}

export const anyExchange: ServerWebExchangeMatcher = async (_exchange: ServerWebExchange): Promise<MatchResult> => {
    return match();
}
anyExchange.toString = () => 'any-exchange';

const EMPTY_OBJECT = Object.freeze({});
export const NO_MATCH: MatchResult = Object.freeze({match: false, variables: EMPTY_OBJECT});
export const match = (variables: Record<string, string> = EMPTY_OBJECT): MatchResult => {
    return { match: true, variables };
}

export const pattern: ((pattern: string | RegExp, opts?: {method?: string}) => ServerWebExchangeMatcher) = (pattern, opts) => {
    const method = opts?.method;
    const matcher = async (exchange: ServerWebExchange): Promise<MatchResult> => {
        const request = exchange.request;
        const path = request.path;
        if (method !== undefined && request.method !== method) {
            return NO_MATCH;
        }
        if (typeof pattern === 'string') {
            if (path === pattern) {
                return match();
            }
            return NO_MATCH;
        }
        else {
            const match = pattern.exec(path);
            if (match === null) {
                return NO_MATCH;
            }
            return {match: true, variables: {...match.groups}};
        }
    };
    matcher.toString = () => {
        return `pattern(${pattern.toString()}, method=${method ?? '<any>'})`;
    }
    return matcher

}
export const mediaType : ((opts: { mediaTypes: string[], ignoredMediaTypes?: string[], useEquality?: boolean }) => ServerWebExchangeMatcher) = (opts): ServerWebExchangeMatcher => {

    const shouldIgnore = (requestedMediaType: string): boolean => {
        if (opts.ignoredMediaTypes !== undefined) {
            for (const ignoredMediaType of opts.ignoredMediaTypes) {
                if (requestedMediaType === ignoredMediaType || ignoredMediaType === '*/*') {
                    return true;
                }
            }
        }
        return false;
    }

    return async (exchange: ServerWebExchange): Promise<MatchResult> => {
        const request = exchange.request;
        let requestMediaTypes: string[];
        try {
            requestMediaTypes = request.headers.list('accept');
        }
        catch (e) {
            return NO_MATCH;
        }
        for (const requestedMediaType of requestMediaTypes) {
            if (shouldIgnore(requestedMediaType)) {
                continue;
            }
            for (const mediaType of opts.mediaTypes) {
                if (requestedMediaType.startsWith(mediaType)) {
                    return match();
                }
            }
        }

        return NO_MATCH;
    };
}

export const upgradeMatcher: ServerWebExchangeMatcher = async ({request}: ServerWebExchange): Promise<MatchResult> => {
    const upgrade = request.upgrade && request.headers.one('upgrade')?.toLowerCase() === 'websocket';
    return upgrade ? match() : NO_MATCH;
};
upgradeMatcher.toString = () => 'websocket upgrade';
